import { useEffect, useLayoutEffect, useState } from 'react';

/**
 * Function to determine if a media query matches the current window state.
 *
 * @param {string} query - The media query string to evaluate.
 * @returns {boolean} - Returns true if the media query matches, otherwise false.
 *
 * Example usage:
 * const isMobile = getMatches('(max-width: 768px)');
 */
function getMatches(query: string): boolean {
  if (typeof window !== 'undefined') {
    return window.matchMedia(query).matches;
  }
  return false;
}

/**
 * Custom hook to track the state of a media query.
 *
 * @param {string} query - The media query string to evaluate.
 * @returns {boolean | undefined} - Returns true if the media query matches, false if it doesn't, or undefined if the initial load is still in progress.
 *
 * Example usage:
 * const isMobile = useMediaQuery('(max-width: 768px)');
 */
export function useMediaQuery(query: string): boolean | undefined {
  // State to track if the media query matches
  const [hasMatch, setHasMatches] = useState<boolean>(getMatches(query));
  // State to track if the initial load is complete
  const [initialLoad, setInitialLoad] = useState(true);

  /**
   * useLayoutEffect to handle the initial load state.
   * It ensures that the initial load state is set to false after the first render.
   */
  useLayoutEffect(() => {
    if (!initialLoad) {
      return;
    }
    setInitialLoad(false);
  }, [initialLoad]);

  /**
   * Function to update the match state when the media query changes.
   */
  function handleChange() {
    setHasMatches(getMatches(query));
  }

  /**
   * Function to set up the media query listener and handle changes.
   * It adds an event listener for changes to the media query and cleans up on unmount.
   *
   * @returns {Function} - A cleanup function to remove the event listener.
   */
  function handleWindowChange() {
    const matchMedia = window.matchMedia(query);
    handleChange();
    matchMedia.addEventListener('change', handleChange);
    return () => {
      matchMedia.removeEventListener('change', handleChange);
    };
  }

  /**
   * useEffect to set up the media query listener when the component mounts
   * and clean up when it unmounts.
   */
  useEffect(
    handleWindowChange,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [query],
  );

  // Return undefined during the initial load, otherwise return the match state
  return initialLoad ? undefined : hasMatch;
}
